import type { AMIDataPoint } from '../types/project';
import { supabase } from './supabase';

const STORE_NAME = 'ami';
const DB_VERSION = 1;

interface AmiRecord {
  projectId: string;
  amiData: AMIDataPoint[];
  savedAt: string;
}

async function getUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
}

function dbName(userId: string): string {
  return `trueload_${userId}`;
}

// Per-user DB connection cache — one open connection per user ID.
const _dbCache = new Map<string, Promise<IDBDatabase>>();

function openDb(userId: string): Promise<IDBDatabase> {
  const name = dbName(userId);
  if (_dbCache.has(name)) return _dbCache.get(name)!;

  const promise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(name, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_NAME)) {
        req.result.createObjectStore(STORE_NAME, { keyPath: 'projectId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      _dbCache.delete(name); // allow retry on next call
      reject(req.error);
    };
  });

  _dbCache.set(name, promise);
  return promise;
}

export async function putAmi(projectId: string, amiData: AMIDataPoint[]): Promise<void> {
  const userId = await getUserId();
  if (!userId) return;
  const db = await openDb(userId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const record: AmiRecord = { projectId, amiData, savedAt: new Date().toISOString() };
    tx.objectStore(STORE_NAME).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function getAmi(projectId: string): Promise<AMIDataPoint[] | null> {
  const userId = await getUserId();
  if (!userId) return null;
  const db = await openDb(userId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(projectId);
    req.onsuccess = () => {
      const record = req.result as AmiRecord | undefined;
      resolve(record?.amiData ?? null);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function deleteAmi(projectId: string): Promise<void> {
  const userId = await getUserId();
  if (!userId) return;
  const db = await openDb(userId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(projectId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
